import React from "react";
import {
  ACTIONS_SLOT_CLASS,
  BESIDE_HINT_CLASS,
  LABEL_ROW_CLASS,
} from "../../lib/field-layout.js";
import { FormFieldLabel } from "./FormFieldLabel.js";

/* FormField's label row: today's bare <label>/<legend> (FormFieldLabel), or
   — once a beside hint or an actionsSlot is set — a flex row AROUND it,
   never inside it. A <label>'s accessible name is computed from its own
   subtree only, so keeping the hint and the actionsSlot as siblings is what
   keeps them out of the control's name (formfield-layout-tile). Single-
   control only: a <legend> must stay the fieldset's own direct child to
   keep naming the group, so `tag="legend"` never wraps — hintPlacement and
   actionsSlot are typed `never` on FormField's fieldset member. */

export interface FormFieldLabelRowProps {
  readonly tag: "label" | "legend";
  readonly id?: string | undefined;
  readonly htmlFor?: string | undefined;
  readonly required: boolean;
  readonly label: React.ReactNode;
  readonly besideHint: boolean;
  readonly hint?: React.ReactNode;
  readonly hintId?: string | undefined;
  readonly actionsSlot?: React.ReactNode;
}

export function FormFieldLabelRow({
  tag,
  id,
  htmlFor,
  required,
  label,
  besideHint,
  hint,
  hintId,
  actionsSlot,
}: FormFieldLabelRowProps): React.ReactElement | null {
  if (label == null) return null;
  const labelEl = (
    <FormFieldLabel as={tag} id={id} htmlFor={htmlFor} required={required}>
      {label}
    </FormFieldLabel>
  );
  if (tag !== "label" || (!besideHint && actionsSlot == null)) return labelEl;
  return (
    <div className={LABEL_ROW_CLASS}>
      {labelEl}
      {besideHint && hint != null && (
        <span id={hintId} className={BESIDE_HINT_CLASS}>
          {hint}
        </span>
      )}
      {actionsSlot != null && (
        <span className={ACTIONS_SLOT_CLASS}>{actionsSlot}</span>
      )}
    </div>
  );
}
